import {
  Button,
  cn,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  useIsMobile,
} from "@beweegbaas/ui";
import { getLocalTimeZone, parseDate, today, type CalendarDate } from "@internationalized/date";
import { useAtomSet, useAtomValue } from "@effect/atom-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  retainSearchParams,
  stripSearchParams,
  useRouter,
} from "@tanstack/react-router";
import { Option } from "effect";
import { useEffect, useMemo, useState } from "react";
import { LessonsHeader } from "#/components/lessons-header";
import { containerStyles, Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { DocentFilter } from "#/modules/les.planning/components/docent-filter";
import { DagKiezer, LesurenDag } from "#/modules/les.planning/components/lesuren-dag";
import { BLEED, LesurenWeek } from "#/modules/les.planning/components/lesuren-week";
import { PlanningToolbar } from "#/modules/les.planning/components/planning-toolbar";
import { UndoHint } from "#/modules/les.planning/components/undo-hint";
import { WeekLessen } from "#/modules/les.planning/components/week-lessen";
import { WeekOpmerking } from "#/modules/les.planning/components/week-opmerking";
import {
  clampToSchooljaar,
  getLesdag,
  getSchooljaarRange,
  getWeekDays,
  getWeekStart,
  minutesToLabel,
  parseDateParam,
} from "#/modules/les.planning/domain/calendar";
import { getWeekOpmerking } from "#/modules/les.planning/domain/jaarplanning/vakken";
import {
  canEditLes,
  getPlanningRange,
  type LesplanningLes,
} from "#/modules/les.planning/domain/les";
import { EMPTY_LESROOSTER } from "#/modules/les.planning/domain/lesrooster";
import type { RoosterSlot } from "#/modules/les.planning/domain/rooster";
import { getLesurenGrid, getWeekLessen, type Cel } from "#/modules/les.planning/domain/week";
import { getStepTo, type PreparationStepPath } from "#/modules/les.planning/preparation-steps";
import { RoosterButton } from "#/modules/les.planning/rooster-route";
import {
  jaarplanningQueryOptions,
  planningKlassenQueryOptions,
  planningQueryOptions,
  roosterQueryOptions,
} from "#/modules/les.planning/query.planning";
import {
  PLANNING_SEARCH_DEFAULTS,
  planningSearchSchema,
  type PlanningView,
} from "#/modules/les.planning/schema.search";
import { PlanningProvider, useUndo } from "#/modules/les.planning/services/runtime";
import {
  flashLesIdAtom,
  lastStepAtom,
  moveLesAtom,
  placementOverridesAtom,
  planSlotAtom,
  slotLessenAtom,
  type Cel as Plek,
} from "#/modules/les.planning/week/week-planner";
import { FALLBACK_AANTAL_GROEPEN } from "#/modules/les.start-flow/filters";
import { useUser } from "#/modules/util.auth/use-user";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/lessons/week")(
  {
    validateSearch: planningSearchSchema,
    // The colleagues go along to Mijn lesdag and back.
    search: {
      middlewares: [stripSearchParams(PLANNING_SEARCH_DEFAULTS), retainSearchParams(["docenten"])],
    },
    loaderDeps: ({ search }) => ({ week: getWeekStart(parseDateParam(search.date)).toString() }),
    loader: ({ context, params: { school, schoolyear }, deps }) =>
      Promise.all([
        context.queryClient.ensureQueryData(
          planningQueryOptions({ school, schoolyear }, getPlanningRange(parseDate(deps.week))),
        ),
        context.queryClient.ensureQueryData(roosterQueryOptions({ school, schoolyear })),
        context.queryClient.ensureQueryData(jaarplanningQueryOptions({ school, schoolyear })),
        context.queryClient.ensureQueryData(planningKlassenQueryOptions({ school, schoolyear })),
      ]),
    component: LesweekPage,
  },
);

function LesweekPage() {
  const { school, schoolyear } = Route.useParams();
  return (
    <PlanningProvider tenant={{ school, schoolyear }}>
      <Lesweek />
    </PlanningProvider>
  );
}

const newSeed = () => Math.random().toString(36).substring(2, 15);

function Lesweek() {
  const { locale, school, schoolyear } = Route.useParams();
  const tenant = { school, schoolyear };
  // Its own params only: a dialog over the page keeps its draft in the URL as well, and every
  // change to it would render the whole week again.
  const search = Route.useSearch({
    select: ({ date, docenten, view }) => ({ date, docenten, view }),
  });
  const navigate = Route.useNavigate();
  const router = useRouter();
  const isMobile = useIsMobile();
  const user = useUser();
  const timeZone = getLocalTimeZone();

  const range = getSchooljaarRange(schoolyear);
  const view: PlanningView = search.view ?? (isMobile ? "day" : "week");
  const gevraagd = clampToSchooljaar(parseDateParam(search.date, timeZone), range);
  // A single day is always a schooldag: the weekend shows the Monday after it.
  const date = view === "day" ? clampToSchooljaar(getLesdag(gevraagd), range) : gevraagd;
  const weekStart = getWeekStart(date);

  const planningQuery = useQuery({
    ...planningQueryOptions(tenant, getPlanningRange(weekStart, timeZone)),
    placeholderData: keepPreviousData,
  });
  const roosterQuery = useQuery(roosterQueryOptions(tenant));
  const jaarplanningQuery = useQuery(jaarplanningQueryOptions(tenant));
  const lesrooster = roosterQuery.data?.lesrooster ?? EMPTY_LESROOSTER;
  const regels = roosterQuery.data?.regels ?? [];
  const docenten = roosterQuery.data?.docenten ?? [];

  const placementOverrides = useAtomValue(placementOverridesAtom);
  const slotLessen = useAtomValue(slotLessenAtom);
  const flashLesId = useAtomValue(flashLesIdAtom);
  const lessenData = planningQuery.data?.lessen;
  const lessen = useMemo(
    () => [
      ...(lessenData ?? []).map((les) => ({ ...les, ...placementOverrides[les.id] })),
      ...Object.values(slotLessen),
    ],
    [lessenData, placementOverrides, slotLessen],
  );

  // Statuses follow the clock: a les that is over turns green or grey.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  // The weekend has no lesuren; a les in it is only in the Verleden and Alles tables.
  const schooldagen = getWeekDays(weekStart, 5).filter(
    (day) => day.compare(range.start) >= 0 && day.compare(range.end) <= 0,
  );
  const vakanties = jaarplanningQuery.data?.vakanties ?? [];
  const grid = getLesurenGrid({
    lesrooster,
    weekStart,
    days: view === "day" ? [date] : schooldagen,
    lessen,
    regels,
    docenten,
    colleagueIds: search.docenten,
    vakanties,
    now,
    timeZone,
    hiddenSlotKeys: Object.keys(slotLessen),
  });
  const weekLessen = getWeekLessen({
    jaarplanning: jaarplanningQuery.data,
    weekStart,
    regels,
    today: today(timeZone),
  });
  const weekOpmerking = jaarplanningQuery.data
    ? getWeekOpmerking(jaarplanningQuery.data, weekStart.toString())
    : null;

  useUndo();
  const moveLes = useAtomSet(moveLesAtom);
  const planSlot = useAtomSet(planSlotAtom);
  const getLastStep = useAtomSet(lastStepAtom, { mode: "promise" });

  function openPreparation(
    step: PreparationStepPath,
    preparation: {
      editLesId?: string;
      klasIds?: string[];
      roosterregelId?: string | null;
      schooldocentId?: string | null;
      plek?: Plek;
    },
  ) {
    const { plek, ...ids } = preparation;
    const schedule = plek && {
      scheduledDate: plek.day.toString(),
      scheduledTime: minutesToLabel(plek.lesuur.startMinuten),
      duurMinuten: plek.lesuur.duurMinuten,
      lesuurId: plek.lesuur.id,
    };
    void router.navigate({
      from: Route.fullPath,
      to: getStepTo("lesweek", step),
      params: { locale, schoolyear, school },
      search: (prev) => ({ ...prev, ...ids, ...schedule }),
    });
  }

  async function openLes(les: LesplanningLes) {
    if (les.id.startsWith("new:")) return;
    // A les that ran once holds results; after a cancelled start it is picked up on its own page.
    if (!canEditLes(les) || les.startedAt != null) {
      void router.navigate({
        to: "/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores",
        params: { locale, schoolyear, school, lesson: les.id },
      });
      return;
    }
    const lastStep = Option.getOrElse(await getLastStep(les.id), () => "activities" as const);
    openPreparation(lastStep, {
      editLesId: les.id,
    });
  }

  function openSlot(slot: RoosterSlot, plek: Plek | null) {
    openPreparation("activities", {
      klasIds: slot.klassen.map((klas) => klas.id),
      roosterregelId: slot.regel.id,
      schooldocentId: slot.regel.isCurrentUser ? null : slot.regel.schooldocentId,
      plek: plek ?? undefined,
    });
  }

  function open(cel: Cel, plek: Plek | null) {
    if (cel.kind === "les") void openLes(cel.les);
    else openSlot(cel.slot, plek);
  }

  function plan(plek: Plek) {
    openPreparation("class", {
      klasIds: [],
      roosterregelId: null,
      schooldocentId: null,
      plek,
    });
  }

  function move(cel: Cel, from: Plek, to: Plek) {
    if (cel.kind === "les") {
      moveLes({ les: cel.les, from, to, timeZone });
      return;
    }
    planSlot({
      slot: cel.slot,
      to,
      timeZone,
      filter: {
        aantalGroepen: user?.defaultAantalGroepen ?? FALLBACK_AANTAL_GROEPEN,
        groepeerType: "Heterogeen",
        seed: newSeed(),
        lockedLeerlingen: [],
        excludedLeerlingen: [],
      },
    });
  }

  function setDate(next: CalendarDate) {
    void navigate({
      search: (prev) => ({ ...prev, date: clampToSchooljaar(next, range).toString() }),
      replace: true,
    });
  }

  function setColleagues(colleagueIds: string[]) {
    void navigate({
      search: (prev) => ({ ...prev, docenten: colleagueIds }),
      replace: true,
      resetScroll: false,
    });
  }

  return (
    <Page>
      <LessonsHeader actions={null} />
      <Page.Body className={cn(containerStyles({ container: "default" }), "mt-4 pb-10")}>
        {lesrooster.lesuren.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m["planning.week.geen_lesuren"]()}</EmptyTitle>
              <EmptyDescription>{m["planning.week.geen_lesuren_uitleg"]()}</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                variant="outline"
                size="xl"
                nativeButton={false}
                render={
                  <Link
                    to="/$locale/{-$schoolyear}/$school/lessons/week/rooster"
                    params={{ locale, schoolyear, school }}
                    search={true}
                  />
                }
              >
                {m["lessen.lesdag.rooster_invullen"]()}
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <div className="flex flex-col gap-4">
            <PlanningToolbar
              view={view}
              date={date}
              weekStart={weekStart}
              range={range}
              addon={weekOpmerking ? <WeekOpmerking tekst={weekOpmerking} /> : null}
              actions={
                <>
                  <RoosterButton
                    render={
                      <Link
                        to="/$locale/{-$schoolyear}/$school/lessons/week/rooster"
                        params={{ locale, schoolyear, school }}
                        search={true}
                      />
                    }
                  />
                  <DocentFilter
                    docenten={docenten}
                    colleagueIds={search.docenten}
                    onChange={setColleagues}
                  />
                </>
              }
              className={cn(BLEED, "border-b pb-4")}
              onDateChange={setDate}
            />
            {view === "day" ? (
              <DagKiezer
                weekStart={weekStart}
                day={date}
                vakanties={vakanties}
                onChange={setDate}
              />
            ) : null}
            <WeekLessen
              lessen={weekLessen}
              // The first lesuur's top border closes it off, so it sits right on the lesuren.
              className={cn(BLEED, "py-3", view === "day" ? "-mb-4 border-t" : "-mt-4 border-b")}
            />
            <PendingContent pending={planningQuery.isFetching}>
              {view === "day" ? (
                <LesurenDag grid={grid} flashLesId={flashLesId} onOpen={open} onPlan={plan} />
              ) : (
                <LesurenWeek
                  grid={grid}
                  today={today(timeZone)}
                  minutes={now.getHours() * 60 + now.getMinutes()}
                  flashLesId={flashLesId}
                  onOpen={open}
                  onPlan={plan}
                  onMove={move}
                />
              )}
            </PendingContent>
            <UndoHint />
          </div>
        )}
      </Page.Body>
      <Outlet />
    </Page>
  );
}
